import {
  Button,
  CollapsibleState,
  cx,
  IconCaretDown,
  IconCaretUp,
  IconTrash,
  Tag,
} from '@vtex/admin-ui'
import { useEffect } from 'react'
import { DefaultProps } from 'typings'
import { CONNECTOR, Rule, RuleContent } from 'ui'
import { styles } from './styles'
import { useIntl } from 'react-intl'
import { messages } from './messages'

type CollapsibleProps = {
  state: CollapsibleState
} & DefaultProps

export function CollapsibleRule(props: CollapsibleProps) {
  const { state, children } = props
  const { getToggleProps } = state

  return (
    <Rule connector={CONNECTOR.AND} {...getToggleProps()}>
      <RuleContent fluid className={styles.collapsible}>
        {children}
      </RuleContent>
    </Rule>
  )
}

function Header(props: CollapsibleProps & { onRemove: () => void }) {
  const { state, children, onRemove } = props
  const { visible } = state

  const { $t } = useIntl()

  return (
    <div className={styles.collapsibleHeader}>
      {children}
      <div className={styles.collapsibleHeaderActions}>
        <Button
          data-open={false}
          variant="criticalSecondary"
          icon={<IconTrash />}
          className={cx(styles.removeButton, 'actions')}
          onClick={onRemove}
          aria-label={$t(messages.remove)}
          hidden={visible}
        />
        {visible ? (
          <Button
            data-open={true}
            variant="neutralTertiary"
            className={styles.nonHoverable}
            icon={<IconCaretUp size="small" />}
          />
        ) : (
          <Button
            data-open={true}
            className={styles.nonHoverable}
            variant="neutralTertiary"
            icon={<IconCaretDown size="small" />}
          />
        )}
      </div>
    </div>
  )
}

function Title(props: CollapsibleProps & { count: number }) {
  const { children, count, state } = props
  const { visible } = state

  return (
    <div className={styles.collapsibleTitle}>
      {children}
      {!visible && count > 0 && (
        <Tag className={styles.counter} label={`${count}`} />
      )}
    </div>
  )
}

function Actions(props: DefaultProps) {
  const { children } = props

  return <div className={styles.collapsibleActions}>{children}</div>
}

function Content(props: CollapsibleProps & { focusId: string }) {
  const { state, children, focusId } = props
  const { getCollapseProps, visible } = state

  useEffect(() => {
    if (visible) {
      const timeout = setTimeout(() => {
        document.getElementById(focusId)?.focus()
      })
      return () => clearTimeout(timeout)
    }
  }, [focusId, visible])

  return (
    <div {...getCollapseProps()}>
      <div className={styles.collapsibleContent}>{visible && children}</div>
    </div>
  )
}

CollapsibleRule.Header = Header
CollapsibleRule.Title = Title
CollapsibleRule.Actions = Actions
CollapsibleRule.Content = Content
